/**
 * @file OrderPrimaryAction.tsx
 * @description Главная кнопка заказа — одна на карточку (SC34, SC52; фаза
 * phase19.24 §13–§14).
 *
 * Кнопка бывает двух видов: переход (ссылка на задание, вкладку, якорь) и
 * действие заказа (подтвердить, начать сборку, завершить). Вид решает модель
 * `resolvePrimaryAction`, компонент только рисует.
 *
 * phase19.31: кнопка живёт в одном месте — в верхней панели
 * (`CabinetPageHeader`), на телефоне каркас переносит её в нижнюю полосу. Из
 * полосы «Сейчас» её дубль убран («Отметить оплату» стояла дважды).
 * Вид — признак очереди: `brand` при «вашем ходе», контур при чужом,
 * мягкая красная при проблеме. Размер даёт тема, своих высот нет.
 */

import { Link } from "react-router";

import type { OrderActionKey } from "../../lib/order-actions";
import type { ResolvedPrimaryAction } from "../../lib/order-now";
import { Button } from "../ui/button";

interface Props {
  primary: ResolvedPrimaryAction;
  /** Ход менеджера — главная кнопка экрана; чужой ход — контурная. */
  filled: boolean;
  /** Проблема (недостача, просрочка) — кнопка красная. */
  danger?: boolean;
  disabled?: boolean;
  /** Вызов действия заказа; для кнопки-ссылки не используется. */
  onAction: (action: OrderActionKey) => void;
  className?: string;
}

export function OrderPrimaryAction({ primary, filled, danger = false, disabled = false, onAction, className }: Props) {
  const variant = danger ? "destructive" : filled ? "brand" : "outline";

  if (primary.href) {
    return (
      <Button variant={variant} asChild className={className}>
        <Link to={primary.href}>{primary.label}</Link>
      </Button>
    );
  }

  return (
    <Button
      variant={variant}
      disabled={disabled || !primary.action}
      onClick={() => primary.action && onAction(primary.action)}
      className={className}
    >
      {primary.label}
    </Button>
  );
}
